import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Storefront, ArrowsClockwise, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * Referral card — shows Partner Portal referral info + commission events.
 * Only rendered when the lead has a referral_id OR referral_source==="partner".
 */
export default function ReferralSection({ lead, onUpdate }) {
  const [loading, setLoading] = useState(false);
  if (!lead) return null;
  const hasReferral = lead.referral_id || lead.referral_source === "partner";
  const isAmbiguous = lead.referral_ambiguous;
  if (!hasReferral && !isAmbiguous) return null;

  const relookup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/partner-portal/lookup", {
        phone: lead.phone, lead_id: lead.id,
      });
      toast.success(`Portal returned ${data.count} referral(s)`);
      onUpdate && onUpdate();
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const ref = lead.referral || {};
  const events = lead.partner_commission_events || [];

  return (
    <div className="border border-border bg-white" data-testid="referral-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Salon referral · Partner Portal</div>
          <h3 className="font-display text-base font-semibold tracking-tight flex items-center gap-2 mt-0.5">
            <Storefront size={16} weight="fill" className="text-primary" />
            {ref.partner_name || "Partner-referred lead"}
          </h3>
        </div>
        <button onClick={relookup} disabled={loading} data-testid="referral-relookup"
          className="px-2 py-1 border border-border hover:bg-secondary text-[10px] uppercase tracking-[0.15em] font-bold flex items-center gap-1 disabled:opacity-50">
          <ArrowsClockwise size={11} /> {loading ? "…" : "Re-lookup"}
        </button>
      </div>

      <div className="p-5 space-y-3">
        {isAmbiguous && (
          <div className="border border-amber-500 bg-amber-50 p-3 text-[11px] flex items-start gap-2">
            <Warning size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">Multiple salons referred this phone</div>
              <div className="text-amber-800 mt-1">Pick one to attach — commission will only credit the chosen partner.</div>
              <div className="mt-2 flex flex-col gap-1">
                {(lead.referral_candidates || []).map((c) => (
                  <button key={c.referral_id}
                    onClick={async () => {
                      try {
                        await api.patch(`/leads/${lead.id}`, {
                          referral_id: c.referral_id,
                          referral: {
                            partner_id: c.partner_id, partner_name: c.partner_name,
                            clinic: c.clinic, qr_scan_timestamp: c.qr_scan_timestamp,
                          },
                          referral_ambiguous: false,
                        });
                        toast.success(`Attached to ${c.partner_name}`);
                        onUpdate && onUpdate();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="text-left px-2 py-1 border border-border bg-white hover:bg-secondary font-mono text-[10px]">
                    {c.partner_name} · {new Date(c.qr_scan_timestamp).toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {hasReferral && !isAmbiguous && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
            <Row label="Partner">{ref.partner_name || "—"}</Row>
            <Row label="Clinic">{ref.clinic || "—"}</Row>
            <Row label="QR scanned"><span className="text-muted-foreground">{ref.qr_scan_timestamp ? new Date(ref.qr_scan_timestamp).toLocaleString() : "—"}</span></Row>
            <Row label="Referral ID"><span className="font-mono text-[10px] break-all">{lead.referral_id}</span></Row>
          </dl>
        )}

        {events.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Commission ledger</div>
            <div className="border border-border divide-y divide-border">
              {events.slice().reverse().map((e, i) => (
                <div key={i} className="px-3 py-1.5 flex items-center gap-2 text-[11px]" data-testid={`commission-event-${i}`}>
                  {(e.http_status >= 200 && e.http_status < 300)
                    ? <CheckCircle size={12} className="text-[#008A00]" weight="fill" />
                    : <Warning size={12} className="text-amber-600" />}
                  <span className="font-mono uppercase text-[9px] tracking-[0.15em]">{e.event}</span>
                  <span className="text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
                  {e.treatment && <span className="text-[10px]">· {e.treatment}</span>}
                  {typeof e.amount === "number" && <span className="text-[10px]">· ₹{e.amount}</span>}
                  <span className="ml-auto tabular-nums text-[10px]">{e.http_status || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
